import moment from 'moment';
import { toast } from 'react-toastify';

/**
 * Embedded styles matching SessionBriefModal.scss exactly
 * These styles ensure the PDF export looks identical to the modal
 */
const getEmbeddedStyles = (): string => `
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      margin: 0;
      padding: 20px;
      background: white;
      font-family: 'Courier New', Courier, monospace;
    }

    .session-brief-content {
      background: white;
      padding: 2rem;
      font-family: 'Courier New', monospace;
      color: #2d3748;
      max-width: 600px;
      margin: 0 auto;
    }

    .sb-logo {
      text-align: center;
      margin-bottom: 1.5rem;
    }

    .sb-logo img {
      max-width: 150px;
      height: auto;
    }

    .sb-header {
      text-align: center;
      margin-bottom: 1.5rem;
    }

    .sb-local-name {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0 0 0.5rem 0;
      color: #2d3748;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .sb-title {
      font-size: 1rem;
      font-weight: 600;
      margin: 0 0 0.75rem 0;
      color: #718096;
    }

    .sb-period {
      font-size: 0.85rem;
      color: #718096;
      line-height: 1.5;
    }

    .sb-divider {
      border-top: 2px dashed #e2e8f0;
      margin: 1.5rem 0;
    }

    .sb-section {
      margin-bottom: 1.5rem;
    }

    .sb-section-title {
      font-size: 0.95rem;
      font-weight: 700;
      margin: 0 0 0.75rem 0;
      color: #2d3748;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .sb-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.4rem 0;
      font-size: 0.9rem;
    }

    .sb-row .sb-label {
      color: #2d3748;
      font-weight: 500;
    }

    .sb-row .sb-value {
      color: #2d3748;
      font-weight: 600;
      text-align: right;
    }

    .sb-row.strong {
      font-weight: 700;
      font-size: 1rem;
      padding-top: 0.75rem;
      margin-top: 0.5rem;
      border-top: 1px solid #e2e8f0;
    }

    .sb-row.strong .sb-label,
    .sb-row.strong .sb-value {
      color: #FC7556;
    }

    .sb-footer {
      margin-top: 2rem;
      padding-top: 1rem;
      border-top: 1px solid #e2e8f0;
      font-size: 0.85rem;
      color: #718096;
    }

    .sb-footer-row {
      text-align: center;
      margin-bottom: 0.25rem;
    }

    .sb-user-card {
      background: #f7fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 0.75rem;
    }

    .sb-user-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .sb-user-name {
      font-weight: 700;
      color: #2d3748;
      font-size: 0.95rem;
    }

    .sb-user-count {
      font-size: 0.75rem;
      padding: 0.25rem 0.75rem;
      border-radius: 12px;
      font-weight: 600;
      background: rgba(252, 117, 86, 0.1);
      color: #FC7556;
    }

    .sb-shift-card {
      background: #f7fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 0.75rem;
    }

    .sb-shift-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .sb-shift-name {
      font-weight: 700;
      color: #2d3748;
      font-size: 0.95rem;
    }

    .sb-shift-badge {
      font-size: 0.75rem;
      padding: 0.25rem 0.75rem;
      border-radius: 12px;
      font-weight: 600;
      background: rgba(252, 117, 86, 0.1);
      color: #FC7556;
    }

    .sb-shift-time {
      font-size: 0.85rem;
      color: #718096;
      margin-bottom: 0.75rem;
    }

    .sb-shift-metrics {
      margin-bottom: 1rem;
    }

    .sb-shift-subsection {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px dashed #e2e8f0;
    }

    .sb-subsection-title {
      font-size: 0.85rem;
      font-weight: 700;
      margin: 0 0 0.5rem 0;
      color: #718096;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .sb-user-mini {
      margin-bottom: 0.5rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px dotted #e2e8f0;
    }

    .sb-user-mini:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    .sb-shift-card .sb-row,
    .sb-user-card .sb-row {
      padding: 0.3rem 0;
      font-size: 0.85rem;
    }

    @media print {
      body {
        margin: 0;
        padding: 1rem;
      }
      .session-brief-content {
        padding: 1rem;
      }
    }

    @page {
      margin: 1cm;
    }
  </style>
`;

/**
 * Generates the HTML structure for the PDF document
 */
const generatePDFHtml = (contentHtml: string): string => `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8">
      <title>Resumen de Sesión - ${moment().format('DD-MM-YYYY')}</title>
      ${getEmbeddedStyles()}
    </head>
    <body>
      ${contentHtml}
    </body>
  </html>
`;

/**
 * Opens a new window with the content and triggers the print dialog
 */
const openPrintWindow = (htmlContent: string): Window | null => {
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    toast.error('Por favor permite ventanas emergentes para exportar PDF');
    return null;
  }

  printWindow.document.write(htmlContent);
  printWindow.document.close();

  return printWindow;
};

/**
 * Filters HTML content to only include selected sections
 */
const filterContentBySections = (
  contentElement: HTMLElement,
  selectedSections: Set<string>
): string => {
  // Clone the element to avoid modifying the original
  const clonedElement = contentElement.cloneNode(true) as HTMLElement;
  
  // Get all removable sections
  const allSections = clonedElement.querySelectorAll('[data-section-id]');
  
  // Remove sections that are not selected
  allSections.forEach((section) => {
    const sectionId = section.getAttribute('data-section-id');
    if (sectionId && !selectedSections.has(sectionId)) {
      // Also remove the preceding divider if it exists
      const previousSibling = section.previousElementSibling;
      if (previousSibling && previousSibling.classList.contains('sb-divider')) {
        previousSibling.remove();
      }
      section.remove();
    }
  });

  return clonedElement.innerHTML;
};

/**
 * Export configuration interface
 */
export interface PDFExportConfig {
  contentRef: React.RefObject<HTMLDivElement>;
  selectedSections?: Set<string>; // Optional: filter sections to include
  onExportStart?: () => void;
  onExportEnd?: () => void;
  onError?: (error: Error) => void;
  printDelay?: number; // Delay in milliseconds before triggering print (default: 500)
}

/**
 * Main function to export modal content as PDF with optional section filtering
 * 
 * @param config - Configuration object containing the content reference and optional callbacks
 * @returns Promise that resolves when export is complete
 * 
 * @example
 * ```tsx
 * const contentRef = useRef<HTMLDivElement>(null);
 * const selectedSections = new Set(['header', 'sales-metrics', 'payment-summary']);
 * 
 * const handleExport = async () => {
 *   await exportToPDF({
 *     contentRef,
 *     selectedSections,
 *     onExportStart: () => setExporting(true),
 *     onExportEnd: () => setExporting(false),
 *   });
 * };
 * ```
 */
export const exportToPDF = async (config: PDFExportConfig): Promise<void> => {
  const {
    contentRef,
    selectedSections,
    onExportStart,
    onExportEnd,
    onError,
    printDelay = 500
  } = config;

  // Validate content reference
  if (!contentRef.current) {
    const error = new Error('Content reference is not available');
    console.error('PDF Export Error:', error);
    toast.error('Error: contenido no disponible para exportar');
    onError?.(error);
    return;
  }

  // Trigger export start callback
  onExportStart?.();

  try {
    // Get the HTML content from the ref
    const element = contentRef.current;
    
    // Filter content by selected sections if provided
    let contentHtml: string;
    if (selectedSections && selectedSections.size > 0) {
      contentHtml = filterContentBySections(element, selectedSections);
    } else {
      contentHtml = element.innerHTML;
    }

    // Generate complete HTML document
    const htmlContent = generatePDFHtml(contentHtml);

    // Open print window
    const printWindow = openPrintWindow(htmlContent);

    if (!printWindow) {
      throw new Error('Failed to open print window');
    }

    // Wait for content and styles to load, then trigger print
    setTimeout(() => {
      try {
        printWindow.print();
        printWindow.close();
        toast.success('Exportando a PDF...');
      } catch (printError) {
        console.error('Error during print:', printError);
        toast.error('Error al abrir diálogo de impresión');
        printWindow.close();
      }
    }, printDelay);

  } catch (error) {
    const exportError = error instanceof Error ? error : new Error('Unknown export error');
    console.error('Error exporting PDF:', exportError);
    toast.error('Error exportando PDF');
    onError?.(exportError);
  } finally {
    // Trigger export end callback
    onExportEnd?.();
  }
};

/**
 * Utility function to validate if browser supports PDF export
 */
export const isPDFExportSupported = (): boolean => {
  return typeof window !== 'undefined' && typeof window.print === 'function';
};

/**
 * Utility function to check if popups are blocked
 */
export const checkPopupBlocker = (): boolean => {
  const testWindow = window.open('', '_blank', 'width=1,height=1');
  
  if (!testWindow || testWindow.closed || typeof testWindow.closed === 'undefined') {
    return true; // Popup blocker is active
  }
  
  testWindow.close();
  return false; // Popup blocker is not active
};
